import { useCallback, useState } from 'react';
import { i18n } from '@lingui/core';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { bool, func } from 'prop-types';
import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useSystemTime, { useFormatTimeOffset, useServerBehindClient } from '../../stores/SystemTimeStore';
import { useVersion, useServerName } from '../../stores/EnvironmentStore';

const useStyles = makeStyles((theme) => ({
    productLogo: {
        width: 135,
        height: 18,
    },
    version: {
        display: 'block',
    },
    timeSyncContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        marginBottom: '12px',
    },
    timeSyncMessage: {
        '& svg': {
            marginRight: '8px',
            position: 'relative',
            top: 'calc(50% - 14px)',
        },
    },
    timeSyncInfo: {
        color: theme.palette.info.main,
    },
}));

const timeBehindMessage = t`behind`;
const timeAheadMessage = t`ahead of`;
const timeSyncTitle = t`The time difference between your client and the nGeniusPULSE server`;

function AboutDialog(props) {
    const { open, onOk } = props;
    const theme = useTheme();
    const classes = useStyles();
    const [version] = useVersion();
    const [serverName] = useServerName();
    const [systemTime] = useSystemTime();
    const [formattedTimeOffset] = useFormatTimeOffset();
    const [isBehind] = useServerBehindClient();

    const aheadBehind = i18n._(isBehind ? timeBehindMessage : timeAheadMessage);
    const timeSyncMessage = t`The nGeniusPULSE server time is ${formattedTimeOffset} ${aheadBehind} your client time`;
    const currentYear = new Date().getFullYear();

    return (
        <Dialog id="about-dialog" open={open} onClose={onOk}>
            <DialogTitle>
                <img
                    src={theme.palette.type === 'light' ? theme.productLogoContrast : theme.productLogo}
                    className={classes.productLogo}
                    alt={theme.productName}
                />
                <Typography className={classes.version}>
                    <Trans>Version</Trans>: {version}
                </Typography>
                <Typography className={classes.version}>
                    <Trans>Server</Trans>: {serverName}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {systemTime && Math.abs(systemTime.systemOffset) > 30000 && (
                    <div className={classes.timeSyncContainer} title={i18n._(timeSyncTitle)}>
                        <div className={[classes.timeSyncMessage, classes.timeSyncInfo].join(' ')}>
                            <InfoIcon fontSize="small" />
                        </div>
                        <div>{i18n._(timeSyncMessage)}</div>
                    </div>
                )}
                <Typography>
                    <Trans>Copyright © 2017-{currentYear} NETSCOUT Systems. All rights reserved.</Trans>
                </Typography>
            </DialogContent>
            <DialogActions id="about-dialog-actions">
                <Button id="about-dialog-action-ok" variant="contained" color="primary" onClick={onOk}>
                    <Trans>Ok</Trans>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

AboutDialog.propTypes = {
    open: bool.isRequired,
    onOk: func,
};

AboutDialog.defaultProps = {
    onOk: _.noop,
};

export default AboutDialog;

export function useAboutDialog(message) {
    const [open, setOpen] = useState(false);
    const [callbacks] = useState({
        onOk: () => setOpen(false),
    });
    const showDialog = useCallback(() => {
        setOpen(true);
    }, []);

    const dialog = <AboutDialog open={open} message={message} onOk={callbacks.onOk} />;

    return [dialog, showDialog];
}
